// The live 3D burner beside the questions, in place of the product illustration.
//
// The model is the Sales Burner Configurator (Cody's), vendored into /burner/ and opened
// with ?embed, which shows the 3D view only. This panel maps the answers onto its
// parameters (the product's profile.burnerModel) and posts them over on every change;
// the page draws that burner and reports back what it could not draw as ordered. The same
// mapping makes the drawing + STEP attached to the item on Save (api/customer-files), so
// what the salesperson sees here is what the customer gets.

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useAnswers } from "../state/AnswersContext";
import { modelParamLabel, type BurnerModel } from "../products/ndbBurnerModel";
import type { AnswerState } from "../types";
import { versioned } from "../data/dataVersion";

type Applied = {
  ok: boolean;
  error?: string;
  conflicts?: string[];
  adjusted?: { k: string; asked: unknown; got: unknown }[];
};

// Versioned like /data/*: the hash covers public/burner too (vite.config.ts), so a browser that
// cached the model file fetches the new one when the configurator is re-vendored.
const SRC = versioned("/burner/burner-configurator.html?embed");

function fmt(v: unknown): string {
  return typeof v === "number" ? `${Math.round(v * 1000) / 1000}"` : String(v);
}

export default function BurnerModelPanel({
  burnerModel,
  fallback,
}: {
  burnerModel: (answers: AnswerState) => BurnerModel | null;
  /** Shown until the answers describe a burner (no housing chosen yet). */
  fallback: ReactNode;
}) {
  const { answers } = useAnswers();
  const model = useMemo(() => burnerModel(answers), [burnerModel, answers]);
  return model ? <LiveModel model={model} /> : <>{fallback}</>;
}

/** Owns the iframe, so its ready/applied state starts over whenever the frame does. */
function LiveModel({ model }: { model: BurnerModel }) {
  // The config as a string: answers change on every keystroke, the burner far less often.
  const key = JSON.stringify(model.config);
  const frame = useRef<HTMLIFrameElement>(null);
  // A count, not a flag: a page that loads again (a reload, a dropped connection) starts
  // blank and must be sent the burner again, which a flag already true would not trigger.
  const [loads, setLoads] = useState(0);
  const [applied, setApplied] = useState<Applied | null>(null);
  const [big, setBig] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (e.source !== frame.current?.contentWindow) return;
      const m = e.data;
      if (!m || m.source !== "flameco-burner") return;
      if (m.type === "ready") setLoads((n) => n + 1);
      // Only the answer to the latest request: an older one may land after a newer one.
      else if (m.type === "applied" && m.id === seq.current) setApplied(m.result);
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  useEffect(() => {
    if (!loads) return;
    // Debounced: typing changes the burner on every keystroke, and "ready" and onLoad
    // both arrive for one load -- either way only the last one is sent.
    const t = window.setTimeout(() => {
      seq.current += 1;
      frame.current?.contentWindow?.postMessage(
        { target: "flameco-burner", type: "apply", id: seq.current, config: JSON.parse(key) },
        window.location.origin
      );
    }, 120);
    return () => window.clearTimeout(t);
  }, [loads, key]);

  useEffect(() => {
    if (!big) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setBig(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [big]);

  const differences = useMemo(() => {
    const out = [...model.notes];
    for (const a of applied?.adjusted ?? [])
      out.push(`${modelParamLabel(a.k)}: ordered ${fmt(a.asked)}, drawn ${fmt(a.got)}.`);
    for (const c of applied?.conflicts ?? []) out.push(`Fit-up: ${c}.`);
    if (applied && !applied.ok) out.push(`The model could not be drawn: ${applied.error}`);
    return out;
  }, [model, applied]);

  return (
    <>
      {big && <div className="cpq-model-scrim" onClick={() => setBig(false)} />}
      {/* Expand restyles this box rather than moving the iframe: moving one reloads it. */}
      <div
        className={`cpq-composite cpq-model${big ? " is-big" : ""}`}
        role={big ? "dialog" : undefined}
        aria-modal={big || undefined}
        aria-label={big ? `Burner model ${model.model}` : undefined}
      >
        <header>
          <span>Model</span>
          <span className="cpq-model-code">{model.model}</span>
          <button type="button" className="cpq-model-expand" onClick={() => setBig((b) => !b)}>
            {big ? "Close" : "Expand"}
          </button>
        </header>
        {/* onLoad as well as the page's own "ready", in case "ready" went out before this
            listener existed; the page's own listener is in place by either. */}
        <iframe
          ref={frame}
          src={SRC}
          title="Burner model"
          className="cpq-model-frame"
          onLoad={() => setLoads((n) => n + 1)}
        />
        {differences.length > 0 && (
          <details className="cpq-model-notes">
            <summary>
              {differences.length} difference{differences.length === 1 ? "" : "s"} from the order
            </summary>
            <ul>
              {differences.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </>
  );
}
